import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Button } from "../../../../components/common/button";
import { IconArrowRight, IconAlertTriangle } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useSaleDetails } from "../../../sales/hooks/useSaleDetails";
import { DeliveryProofCard } from "../components/delivery-proof-card";
import { OrderInfoCard } from "../components/order-info-card";
import { OrderItemsCard } from "../components/order-items-card";
import { FinancialBar } from "../components/financial-bar";
import { AdminOrderInvoicesCard } from "../components/admin-order-invoices-card";
import { OrderAddressSection } from "../../../../components/common/order-address-section";
import { salesService } from "../../../api_client";
import type { SaleDetailsDto } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
function formatDate(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : DATE_FMT.format(d);
}

function getStatusChip(order: SaleDetailsDto) {
  if (order.state === "cancel") return <Chip variant="rejected">Cancelled</Chip>;
  if (order.buyerReceived) return <Chip variant="approved">Validated</Chip>;
  const pickings = order.dropshipping ?? [];
  const allDone = pickings.length > 0 && pickings.every((d) => d.state === "done");
  const ds = ((order as unknown as { deliveryStatus?: string }).deliveryStatus ?? "").toLowerCase();
  if (allDone || ds === "full" || ds === "partial" || order.state === "done")
    return <Chip variant="inReview">Delivered</Chip>;
  if (order.sellerShipped) return <Chip variant="underReview">In Transit</Chip>;
  return <Chip variant="tertiary">Processing</Chip>;
}

export function AdminOrderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: order, isLoading, mutate } = useSaleDetails(Number(id));
  const [isActing, setIsActing] = useState(false);
  const [isRouting, setIsRouting] = useState(false);

  const backLabel = (location.state as { from?: string } | null)?.from ?? "Orders";
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(AppRoutes.ADMIN_ORDERS);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 py-6 md:px-8 md:py-8">
        <ShimmerLoader message="Loading order details…" lines={6} />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-4 py-6 md:px-8 md:py-8">
        <Text variant="body" tone="muted">Order not found.</Text>
      </div>
    );
  }

  const placedBy = order.partner?.name ?? "—";
  const placedOn = formatDate(order.dateOrder);

  const handleApprove = async () => {
    if (!order) return;
    setIsActing(true);
    try {
      await salesService.markReceived(order.id);
      await mutate({ ...order, buyerReceived: true }, { revalidate: true });
      toast.success("Delivery approved");
    } catch {
      toast.error("Failed to approve delivery");
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (!order) return;
    setIsActing(true);
    try {
      await salesService.cancelSaleOrder(order.id);
      await mutate();
      toast.success("Order cancelled");
    } catch {
      toast.error("Failed to cancel order");
    } finally {
      setIsActing(false);
    }
  };

  const handleRouteToSuppliers = async () => {
    if (!order) return;
    setIsRouting(true);
    try {
      await salesService.routeSaleOrderToSuppliers(order.id);
      await mutate();
      toast.success("Order routed to suppliers");
    } catch {
      toast.error("Failed to route order to suppliers");
    } finally {
      setIsRouting(false);
    }
  };

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-4 py-6 md:px-8 md:py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 max-md:flex-wrap">
        <button onClick={handleBack} className="hover:underline">
          <Text variant="bodyBold" tone="accent">← {backLabel}</Text>
        </button>
        <Text variant="body" tone="muted">/</Text>
        <Text variant="body" tone="muted">{order.name}</Text>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 max-md:flex-col max-md:gap-2">
        <div className="flex items-center gap-3 max-md:flex-wrap">
          <Heading variant="h2" tone="dark">{order.name}</Heading>
          {getStatusChip(order)}
        </div>
        {placedOn && (
          <Text variant="bodySm" tone="muted">
            Placed by {placedBy} · {placedOn}
          </Text>
        )}
      </div>

      {order.routed === false && (
        <div className="flex items-center gap-4 rounded-2xl border border-accentAmber bg-accentYellowTint px-5 py-4 max-md:flex-wrap max-md:px-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accentAmberDark">
            <IconAlertTriangle size={20} className="text-bgWhite" />
          </div>
          <div className="flex flex-1 flex-col gap-0.5 max-md:min-w-0 max-md:basis-[calc(100%-56px)]">
            <Text variant="bodyBold" tone="inherit" className="!text-accentAmberDark">
              This order hasn't been routed to a supplier yet
            </Text>
            <Text variant="bodySm" tone="inherit" className="!text-accentAmberDark">
              No purchase orders have been created for these items yet. Try routing again to re-run the procurement rules.
            </Text>
          </div>
          <Button variant="secondary" size="sm" onClick={handleRouteToSuppliers} disabled={isRouting}>
            {isRouting ? "Routing…" : "Route to Suppliers"}
          </Button>
        </div>
      )}

      {(order as any).linkedPurchaseOrders?.map((po: { id: number; name: string }) => (
        <button
          key={po.id}
          type="button"
          onClick={() => navigate(AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS.replace(":id", String(po.id)), { state: { from: `Order ${order.name}` } })}
          className="flex w-full items-center justify-between rounded-xl border border-borderDefault bg-bgWhite px-5 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgLight max-md:px-4"
        >
          <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-0">
            <Text variant="bodySmMedium" tone="muted">Linked Purchase Order:</Text>
            <Text variant="bodySmBold" tone="dark">{po.name}</Text>
          </div>
          <IconArrowRight size={16} className="text-textMuted" />
        </button>
      ))}

      <DeliveryProofCard order={order} onApprove={handleApprove} onReject={handleReject} isActing={isActing} />
      <OrderAddressSection
        invoicePartner={order.invoicePartner ?? order.partner ?? null}
        shippingPartner={order.shippingPartner ?? null}
      />
      <OrderInfoCard
        order={order}
        sellerPartner={
          (order as any).linkedPurchaseOrders?.[0]?.partnerId
            ? { id: (order as any).linkedPurchaseOrders[0].partnerId, name: (order as any).linkedPurchaseOrders[0].partnerName ?? "Seller" }
            : null
        }
      />
      <OrderItemsCard order={order} />
      <AdminOrderInvoicesCard saleOrderId={order.id} />
      <FinancialBar order={order} />
    </div>
  );
}
